import { FC } from 'react';

import dayjs from 'dayjs';
import {
  Data,
  DATA_TYPE,
  DATA_STRING_FORMAT,
  DATA_NUMBER_FORMAT,
  DATA_DATE_FORMAT,
} from 'utils/types';
import { isEmpty, isNil } from 'lodash';

import QuickEditDate from './QuickEditDate';
import QuickEditDropdown from './QuickEditDropdown';
import QuickEditInput from './QuickEditInput';
import QuickEditMultiselect from './QuickEditMultiselect';
import QuickEditNumberCurrency from './QuickEditNumberCurrency';
import QuickEditNumberFormatted from './QuickEditNumberFormatted';
import QuickEditNumberPercentage from './QuickEditNumberPercentage';
import QuickEditPhoneNumber from './QuickEditPhoneNumber';

type CustomQuickEditFieldProps = {
  data: Pick<Data, '_id' | 'description' | 'meta' | 'name' | 'type'>;
  dataId: string;
  value: any;
  onSubmit: (id: string, val: any, onSuccess: () => void) => void;
};

const ruleChecker = (
  field: Pick<Data, '_id' | 'description' | 'meta' | 'name' | 'type'>,
  value: any,
) => {
  if (field.type === DATA_TYPE.STRING) {
    if (isNil(value)) return;
    //Check min/max length
    if (field.meta.min && value.length && value.length < field.meta.min) {
      return `Min length of ${field.meta.min} characters required`;
    }
    if (field.meta.max && value.length > field.meta.max) {
      return `Max length of ${field.meta.max} characters required`;
    }
  }
  if (field.type === DATA_TYPE.NUMBER) {
    if (isEmpty(value)) return;
    //Check min/max length
    if (field.meta.min && !isNil(value) && +value < field.meta.min) {
      return `Min value of ${field.meta.min} required`;
    }
    if (field.meta.max && +value > field.meta.max) {
      return `Max value of ${field.meta.max} required`;
    }
  }
};

const CustomQuickEditField: FC<CustomQuickEditFieldProps> = ({
  data,
  value,
  onSubmit,
}) => {
  switch (data?.type) {
    case DATA_TYPE.STRING:
      return (
        <QuickEditInput
          isTextArea={data.meta.format === DATA_STRING_FORMAT.LONGSTRING}
          rule={(val) => {
            return ruleChecker(data, val);
          }}
          onSubmit={(val, onSuccess) => {
            return onSubmit(data._id, isEmpty(val) ? null : val, onSuccess);
          }}
          title={data.name}
          value={value}
        />
      );
    case DATA_TYPE.MOBILENUMBER: {
      return (
        <QuickEditPhoneNumber
          rule={(val) => ruleChecker(data, val)}
          onSubmit={(val, onSuccess) => {
            return onSubmit(data._id, val, onSuccess);
          }}
          title={data.name}
          value={value}
        />
      );
    }
    case DATA_TYPE.BOOLEAN: {
      return (
        <QuickEditDropdown
          value={value}
          options={[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]}
          onSubmit={(val, onSuccess) =>
            onSubmit(data._id, val.value, onSuccess)
          }
          title={data.name}
        />
      );
    }

    case DATA_TYPE.RADIOBOX:
    case DATA_TYPE.SELECT: {
      return (
        <QuickEditDropdown
          value={value}
          options={data.meta.options ?? []}
          onSubmit={(val, onSuccess) =>
            onSubmit(data._id, val.value, onSuccess)
          }
          title={data.name}
        />
      );
    }
    case DATA_TYPE.CHECKBOX:
    case DATA_TYPE.MULTISELECT: {
      return (
        <QuickEditMultiselect
          title={data.name}
          options={data.meta.options ?? []}
          onSubmit={(val, onSuccess) => onSubmit(data._id, val, onSuccess)}
          values={value}
        />
      );
    }
    case DATA_TYPE.NUMBER: {
      if (
        (data.meta.format as DATA_NUMBER_FORMAT) ===
        DATA_NUMBER_FORMAT.UNFORMATTED
      ) {
        return (
          <QuickEditInput
            isNumber
            rule={(val) => ruleChecker(data, val)}
            onSubmit={(val, onSuccess) => {
              return onSubmit(data._id, isEmpty(val) ? null : +val, onSuccess);
            }}
            title={data.name}
            value={value}
          />
        );
      }
      if (
        (data.meta.format as DATA_NUMBER_FORMAT) ===
        DATA_NUMBER_FORMAT.FORMATTED
      ) {
        return (
          <QuickEditNumberFormatted
            rule={(val) => ruleChecker(data, val)}
            onSubmit={(val, onSuccess) => {
              return onSubmit(data._id, isEmpty(val) ? null : +val, onSuccess);
            }}
            title={data.name}
            value={value}
          />
        );
      }
      if (
        (data.meta.format as DATA_NUMBER_FORMAT) === DATA_NUMBER_FORMAT.CURRENCY
      ) {
        return (
          <QuickEditNumberCurrency
            rule={(val) => ruleChecker(data, val)}
            onSubmit={(val, onSuccess) => {
              return onSubmit(data._id, isEmpty(val) ? null : +val, onSuccess);
            }}
            title={data.name}
            value={value}
          />
        );
      }
      if (
        (data.meta.format as DATA_NUMBER_FORMAT) ===
        DATA_NUMBER_FORMAT.PERCENTAGE
      ) {
        return (
          <QuickEditNumberPercentage
            rule={(val) => ruleChecker(data, val)}
            onSubmit={(val, onSuccess) => {
              return onSubmit(data._id, isEmpty(val) ? null : +val, onSuccess);
            }}
            title={data.name}
            value={value}
          />
        );
      }
      break;
    }
    case DATA_TYPE.DATE: {
      const _value =
        data.meta.format === DATA_DATE_FORMAT.DATE
          ? dayjs(value).format('MM/DD/YYYY')
          : dayjs(value).format('MM/DD/YYYY hh:mm:A');
      return (
        <QuickEditDate
          format={data.meta.format as DATA_DATE_FORMAT}
          onSubmit={(val, onSuccess) => {
            return onSubmit(data._id, val, onSuccess);
          }}
          title={data.name}
          value={dayjs(_value).isValid() ? _value : undefined}
        />
      );
    }
  }
};

export default CustomQuickEditField;
